import {
  agentTemplateById,
  matchAgentTemplate,
} from '../../utils/agent-templates';

/** Joining words a title keeps lower case, unless they start it. */
const SMALL_WORDS = new Set([
  'a',
  'an',
  'and',
  'as',
  'at',
  'by',
  'for',
  'in',
  'of',
  'on',
  'or',
  'the',
  'to',
  'with',
]);

/**
 * Title Case, as the agent templates are named ("Start a Return"). A word
 * that already has a capital after its first letter ("ID", "eSIM") is left
 * as written.
 */
export const titleCase = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word, i) => {
      if (/[A-Z]/.test(word.slice(1))) return word;
      const lower = word.toLowerCase();
      if (i > 0 && SMALL_WORDS.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');

/**
 * Talk it through's tasks, named the same way every time. The model words a
 * task differently from one run to the next ("Order Status", "Check order
 * status", "Check Order Status") even when it picks the same template, so a
 * task a template builds takes that template's name, and any other is put in
 * Title Case. A name another task already has stays the task's own.
 */
export const predictableTaskNames = <
  T extends { name: string; phrases: string[]; template?: string },
>(
  tasks: T[]
): T[] => {
  const taken = new Set<string>();
  return tasks.map((task) => {
    const own = titleCase(task.name);
    const template =
      agentTemplateById(task.template) ??
      matchAgentTemplate(task.name, task.phrases);
    const name =
      template && !taken.has(template.name.toLowerCase()) ? template.name : own;
    taken.add(name.toLowerCase());
    return { ...task, name };
  });
};
